import React, { useMemo } from "react";
import {
  CartesianGrid,
  Label,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { snapshotAvailableMetrics } from "../../reports/agency/ToggleSnapshotMetrics";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const transformSnapshotData = (snapshotData, metric) => {
  if (
    !snapshotData?.previous_year ||
    !snapshotData?.current_year ||
    !snapshotData?.meta
  ) {
    return [];
  }

  const startMonth = parseInt(snapshotData.meta.start_month, 10);
  if (isNaN(startMonth) || startMonth < 1 || startMonth > 12) return [];

  const orderedMonths = Array.from({ length: 12 }, (_, i) => {
    const monthNum = ((startMonth - 1 + i) % 12) + 1;
    return monthNum.toString().padStart(2, "0");
  });

  const flattenData = data => {
    const flat = {};
    for (const year in data) {
      for (const month in data[year]) {
        flat[month] = data[year][month]?.[metric] ?? 0;
      }
    }
    return flat;
  };

  const previousFlat = flattenData(snapshotData.previous_year);
  const currentFlat = flattenData(snapshotData.current_year);

  return orderedMonths.map(monthKey => ({
    name: MONTH_NAMES[parseInt(monthKey, 10) - 1],
    previous: previousFlat[monthKey] || 0,
    current: currentFlat[monthKey] || 0,
  }));
};

const SnapshotLineChart = ({ snapshotData, metric = "event_count" }) => {
  const chartData = useMemo(() => {
    return transformSnapshotData(snapshotData, metric);
  }, [snapshotData, metric]);

  const isCurrencyMetric = metric =>
    ["event_value", "confirmed_event_value", "commission_value"].includes(
      metric
    );

  const valueFormatter = value => {
    if (isCurrencyMetric(metric)) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 0,
      }).format(value);
    }
    return value.toLocaleString();
  };

  const getYAxisLabel = metric => {
    return snapshotAvailableMetrics.find(
      snapshotMetric => snapshotMetric.key === metric
    ).label;
    if (
      ["event_value", "confirmed_event_value", "commission_value"].includes(
        metric
      )
    )
      return "£";
    if (["commission_rate", "conversion_rate"].includes(metric)) return "%";
    return "";
  };

  return (
    <ResponsiveContainer width="100%" height={500}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 5, left: 45, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <LabelList dataKey="name" position="left" />
        <YAxis tickFormatter={valueFormatter}>
          <Label
            value={getYAxisLabel(metric)}
            angle={-90}
            position="left"
            offset={40}
            style={{ textAnchor: "middle" }}
          />
        </YAxis>
        <Tooltip formatter={valueFormatter} />
        <Legend />
        <Line
          type="monotone"
          dataKey="previous"
          stroke="#555"
          name="Previous Year"
          dot={{ r: 5 }}
        />
        <Line
          type="monotone"
          dataKey="current"
          stroke="#0099ff"
          name="Current Year"
          dot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default SnapshotLineChart;
